import React, { useCallback } from 'react';

import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { noop } from 'ui-components/default-props/noop';

import style from './multi-select-dropdown.module.less';

/**
 * @typedef {object} MultiSelectOption
 * @property {string} id - Unique identifier for the option
 * @property {string} value - Value of the option
 * @property {React.ReactNode} component - React component to display for the option
 */

/**
 * Multi-select dropdown component
 * @param {object} props
 * @param {MultiSelectOption[]} props.options - Array of options to display
 * @param {MultiSelectOption[]} props.selectedOptions - Array of currently selected options
 * @param {string | React.ReactNode} props.label - Label to display on the dropdown toggle (can be string or JSX)
 * @param {(option: MultiSelectOption) => void} props.onOptionClicked - Callback when an option is clicked
 * @param {boolean} [props.shouldSendFocusToInput] - Whether to send focus to input
 * @returns {JSX.Element}
 */
const MultiSelectDropdown = ({ options, selectedOptions, label, onOptionClicked, shouldSendFocusToInput = false }) => {
  const isOptionChecked = useCallback(
    (option) => {
      return !!(selectedOptions || []).find((opt) => opt.id === option.id);
    },
    [selectedOptions],
  );

  return (
    <DropdownMenu className={style['multi-select-dropdown-wrapper']} closeWhenChildrenClicked={false} dropdownMenuShouldHaveWidthOfButton>
      <DropdownMenuToggler className={style['dropdown-toggler']} shouldSendFocusToInput={shouldSendFocusToInput} theme='tertiary'>
        {label}
      </DropdownMenuToggler>
      {options.map((option) => (
        <DropdownMenuItem key={option.id} className={style['checklist-row']} onClick={() => onOptionClicked(option)}>
          <Checkbox checked={isOptionChecked(option)} label={option.component} onChange={noop} />
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

export default MultiSelectDropdown;
